<template>
  <SideMenuLayout title="Prestations" :items="items">
    <slot/>
  </SideMenuLayout>
</template>

<script>

import {BUSINESS_TABS, BUSINESS_COUNTS_MAX_AGE, COUNTS_REFRESH_EVENT, businessCountsCache} from "@/components/businessTabs";
import SideMenuLayout from "@/components/SideMenuLayout.vue";

import {BusinessController} from "@/controllers/business/BusinessController";

const ICONS = {all: 'list', quotes: 'file', unpaid: 'receipt', deposits: 'wallet', 'credit-notes': 'outgoing'};

/**
 * Menu latéral des contrats (Tous, Devis, Acomptes, Factures, Avoirs, Payés) ; le contenu de la page se place à l'intérieur.
 */
export default {
  name: "BusinessesHeader",
  components: {SideMenuLayout},
  data() {
    return {refreshTimer: undefined};
  },
  computed: {
    // Valeurs partagées entre les pages (le menu est recréé à chaque changement de rubrique)
    counts() {
      return businessCountsCache.token === localStorage.token ? businessCountsCache.counts : undefined;
    },
    items() {
      return BUSINESS_TABS.map(tab => ({to: tab.path, label: tab.label, icon: ICONS[tab.key], separatorAfter: tab.separatorAfter, active: this.$route.path === tab.path, badges: this.badgesOf(tab.key)}));
    }
  },
  mounted() {
    // Pas de requête quand des valeurs récentes sont déjà connues : elles sont mises à jour par les changements de données
    if (!this.counts || Date.now() - businessCountsCache.loadedAt > BUSINESS_COUNTS_MAX_AGE) this.loadCounts();
    window.addEventListener(COUNTS_REFRESH_EVENT, this.scheduleRefresh);
  },
  beforeUnmount() {
    window.removeEventListener(COUNTS_REFRESH_EVENT, this.scheduleRefresh);
    clearTimeout(this.refreshTimer);
  },
  methods: {
    async loadCounts() {
      const resp = await BusinessController.getTabCounts();
      if (resp && !resp.errors) Object.assign(businessCountsCache, {counts: resp, loadedAt: Date.now(), token: localStorage.token});
    },
    // Les tableaux émettent l'événement après chaque chargement : un seul rechargement des compteurs pour une rafale d'appels
    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(this.loadCounts, 300);
    },
    formatAmount(value) {
      return (value || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €';
    },
    // Pastilles d'une rubrique (une ou plusieurs)
    badgesOf(key) {
      if (!this.counts) return undefined;
      const plural = (count, one, many) => count + ' ' + (count > 1 ? many : one);
      const badges = {
        // Devis : non envoyés (à traiter) en orange, envoyés (en cours) en bleu
        quotes: [
          {count: this.counts.unsentQuotes, tone: 'warn', title: plural(this.counts.unsentQuotes, 'devis non envoyé', 'devis non envoyés')},
          {count: this.counts.sentQuotes, tone: 'info', title: plural(this.counts.sentQuotes, 'devis envoyé ou en cours', 'devis envoyés ou en cours')},
        ],
        deposits: {count: this.counts.depositsToCollect, tone: 'warn', title: plural(this.counts.depositsToCollect, 'acompte à encaisser', 'acomptes à encaisser') + ' (' + this.formatAmount(this.counts.depositsToCollectAmount) + ')'},
        // Avoirs : à émettre (en rédaction) en orange, à rembourser (émis, argent dû au client) en rouge
        'credit-notes': [
          {count: this.counts.creditNotesToIssue, tone: 'warn', title: plural(this.counts.creditNotesToIssue, 'avoir à émettre (en rédaction)', 'avoirs à émettre (en rédaction)')},
          {count: this.counts.creditNotesToRefund, tone: 'danger', title: plural(this.counts.creditNotesToRefund, 'avoir à rembourser', 'avoirs à rembourser') + ' (' + this.formatAmount(this.counts.creditNotesToRefundAmount) + ')'},
        ],
        // Factures : impayés échus en rouge, impayés pas encore échus (en attente de paiement) en bleu
        unpaid: [
          {count: this.counts.unpaidInvoices, tone: 'danger', title: plural(this.counts.unpaidInvoices, 'facture impayée échue', 'factures impayées échues')},
          {count: this.counts.pendingInvoices, tone: 'info', title: plural(this.counts.pendingInvoices, 'facture en attente de paiement (échéance non dépassée)', 'factures en attente de paiement (échéance non dépassée)')},
        ],
      };
      const badge = badges[key];
      return badge && !Array.isArray(badge) ? [badge] : badge;
    }
  }
};
</script>
